/* eslint-disable no-restricted-globals */
/* eslint-disable no-void */
import { useMemo, useState, useContext } from "react"

import { useI18next } from "gatsby-plugin-react-i18next"
import styled, { css } from "styled-components"

import { TranslateURLContext } from "components/Providers"
import { ReactComponent as ChevSVG } from "images/global/chevron.svg"
import { ReactComponent as EnFlagSVG } from "images/global/enLangFlag.svg"
import { ReactComponent as EsFlagSVG } from "images/global/esLangFlag.svg"
import { ReactComponent as PtFlagSVG } from "images/global/ptLangFlag.svg"
import colors from "styles/colors"
import easing from "styles/easing"
import text from "styles/text"
import { fresponsive } from "utils/fullyResponsive"
import UniversalLink from "utils/Loader/UniversalLink"

interface LanguageButtonProps {
  handleToggleFollowDiv?: () => void
  pathname: string
  languageOverride?: string
}

const renderLanguageIcon = (abbr: string) => {
  if (abbr === "es") {
    return (
      <LangIcon>
        <EsFlagSVG />
      </LangIcon>
    )
  }

  if (abbr === "pt") {
    return (
      <LangIcon>
        <PtFlagSVG />
      </LangIcon>
    )
  }

  return (
    <LangIcon>
      <EnFlagSVG />
    </LangIcon>
  )
}

const languages = [
  { abbr: "en", name: "English" },
  { abbr: "es", name: "Spanish" },
  { abbr: "pt", name: "Portuguese" },
]

export default function LanguageButton({
  handleToggleFollowDiv,
  pathname,
  languageOverride,
}: LanguageButtonProps) {
  const { allLocaleURLS } = useContext(TranslateURLContext)

  const { language } = useI18next()
  const [isOpen, setIsOpen] = useState(false)
  const [showDiv, setShowDiv] = useState(false)

  const handleShowLanguagesDiv = () => {
    setShowDiv(!showDiv)
  }

  const allLanguageCards = useMemo(
    () =>
      languages
        .filter(lng1 => lng1.abbr !== (languageOverride ?? language))
        .map(lng => {
          const translatedSlug =
            allLocaleURLS?.filter(url => url.node_locale === lng.abbr)[0]
              .urlSlug ?? ""

          const pathnames = pathname
            .split("/")
            .filter(
              pathnameChild =>
                pathnameChild !== "es" &&
                pathnameChild !== lng.abbr &&
                pathnameChild !== "pt" &&
                pathnameChild !== lng.abbr &&
                pathnameChild !== "en" &&
                pathnameChild !== ""
            )

          const parsedPathnames = translatedSlug
            ? pathnames.slice(0, pathnames.length - 1).join("/")
            : pathnames.join("/")

          const root =
            pathnames.length <= 1 ||
            pathnames[0] !== (languageOverride ?? language)

          const finalPathName = `${translatedSlug || root ? "/" : ""}${
            lng.abbr === "en" ? "" : `${lng.abbr}/`
          }${parsedPathnames}${parsedPathnames && "/"}${translatedSlug}`

          return (
            <Wrapper key={lng.abbr} role="presentation">
              <LangCard to={finalPathName} key={lng.abbr}>
                {renderLanguageIcon(lng.abbr)}
                <Lang>{lng.name}</Lang>
              </LangCard>
            </Wrapper>
          )
        }),
    [allLocaleURLS, language, pathname, languageOverride]
  )

  return (
    <LangSelector
      isOpen={isOpen}
      role="listbox"
      tabIndex={0}
      onClick={() => {
        setIsOpen(!isOpen)
        handleShowLanguagesDiv()
        handleToggleFollowDiv?.()
      }}
      onKeyDown={() => {
        setIsOpen(!isOpen)
      }}
    >
      <Wrapper>
        <LangCardDiv>
          {renderLanguageIcon(languageOverride ?? language)}
          <Lang>
            {languages.find(
              lang => lang.abbr === (languageOverride ?? language)
            )?.name ?? ""}
          </Lang>
        </LangCardDiv>
        <ArrowSection isOpen={isOpen}>
          <ChevSVG />
        </ArrowSection>
      </Wrapper>
      <Content show={showDiv}>{isOpen && allLanguageCards}</Content>
    </LangSelector>
  )
}

const LangIcon = styled.div`
  ${fresponsive(css`
    width: 30px;
    height: 30px;
    display: flex;
    margin-right: 10px;
  `)}
`

const Wrapper = styled.div`
  ${fresponsive(css`
    display: flex;
    width: 150px;
    align-items: center;
    gap: 40px;
    flex-shrink: 0;
  `)}
`

const LangCard = styled(UniversalLink)`
  ${fresponsive(css`
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    gap: 10px;
    color: ${colors.lightMode};
    transition: 0.3s;
    :hover {
      transition: 0.3s;
      color: ${colors.blue10};
    }
  `)}
`

const Lang = styled.div`
  text-transform: capitalize;
  ${fresponsive(css`
    ${text.buttonR}
  `)}
`

const LangSelector = styled.div<{ isOpen: boolean }>`
  cursor: pointer;
  box-sizing: border-box;
  //Colors -----------------------------------
  background-color: transparent;
  color: ${colors.white};
  //Colors -----------------------------------
  height: ${props => (props.isOpen ? "122px" : "30px")}
  transition: 300ms;
  transition-property: height;
  transition-timing-function: ${easing.button};

  ${fresponsive(css`
    display: flex;
    width: 150px;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 16px;
  `)}
`

const LangCardDiv = styled.div`
  ${fresponsive(css`
    display: flex;
    align-items: center;
    gap: 10px;
    color: ${colors.lightMode};
    transition: 0.3s;
    :hover {
      transition: 0.3s;
      color: ${colors.blue10};
    }
  `)}
`
const ArrowSection = styled.div<{ isOpen: boolean }>`
  svg {
    transition-duration: 300ms;
    transition-timing-function: ${easing.button};
    transform: rotate(${props => (props.isOpen ? "90deg" : "-90deg")});
    width: 100%;
  }

  ${fresponsive(css`
    width: 15px;
    height: 15px;
    flex-shrink: 0;
  `)}
`

const Content = styled.div<{ show: boolean }>`
  opacity: ${({ show }) => (show ? 1 : 0)};
  transition: opacity 0.4s;
`
